import React from 'react';
import { FiMessageSquare, FiArrowUpRight, FiUser, FiFileText, FiThumbsUp, FiThumbsDown, FiTag } from 'react-icons/fi';
import IconWrapper from './IconWrapper';

type SentimentType = 'Positive' | 'Negative' | 'Very Negative' | 'Neutral' | 'Mixed' | string;

interface VerbatimCardProps {
  text: string;
  sentiment: SentimentType;
  sentimentScore: number;
  emotions: string[];
  keywords: string[];
  onClick?: () => void;
}

const VerbatimCard: React.FC<VerbatimCardProps> = ({ 
  text,
  sentiment,
  sentimentScore,
  emotions,
  keywords,
  onClick
}) => {
  // Helper to get sentiment color
  const getSentimentColor = (sentiment: SentimentType, score: number) => {
    if (sentiment.includes('Negative')) {
      return score < 2 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
    } else if (sentiment === 'Neutral') {
      return 'bg-gray-100 text-gray-800';
    } else if (sentiment.includes('Positive')) {
      return 'bg-green-100 text-green-800';
    } else {
      return 'bg-blue-100 text-blue-800';
    }
  };
  
  // Helper to get sentiment icon
  const getSentimentIcon = (sentiment: SentimentType) => {
    if (sentiment.includes('Negative')) {
      return FiThumbsDown;
    } else if (sentiment.includes('Positive')) {
      return FiThumbsUp;
    } else {
      return FiMessageSquare;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          <div className={`rounded-full p-1.5 mr-2 ${getSentimentColor(sentiment, sentimentScore).replace('text-', 'text-').replace('bg-', 'bg-')}`}>
            <IconWrapper icon={getSentimentIcon(sentiment)} size={16} className={getSentimentColor(sentiment, sentimentScore).replace('bg-', 'text-')} />
          </div>
          <div>
            <div className="flex items-center">
              <span className={`text-xs px-2 py-0.5 rounded-full ${getSentimentColor(sentiment, sentimentScore)}`}>
                {sentiment}
              </span>
              <span className="mx-2 text-xs text-secondary/50">•</span>
              <span className="text-xs text-secondary/70">Score: {sentimentScore.toFixed(1)}/10</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mb-4">
        <p className="text-sm text-secondary">{text}</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-secondary/70 mb-1">Top Emotions:</div>
          <div className="flex flex-wrap gap-1">
            {emotions.map((emotion, index) => (
              <span key={index} className="text-xs bg-secondary/5 px-2 py-0.5 rounded">
                {emotion}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-secondary/70 mb-1">Keywords:</div>
          <div className="flex flex-wrap gap-1">
            {keywords.map((keyword, index) => (
              <span key={index} className="text-xs bg-primary/5 text-primary px-2 py-0.5 rounded flex items-center">
                <IconWrapper icon={FiTag} size={10} className="mr-1" />
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerbatimCard;